import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
} from '@cca/ui';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';
import { useRuntimeConfig } from '../lib/runtime-config.js';
import { RequireSession } from '../components/require-session.js';
import { rootRoute } from './root.js';

interface PendingApproval {
  readonly id: string;
  readonly state: string;
  readonly approved_version: number;
  readonly approved_recipient_count: number;
  readonly requested_by: string;
  readonly requested_at: string;
  readonly expires_at: string;
  readonly required_permission: string;
  readonly communication_drafts: {
    readonly id: string;
    readonly subject: string | null;
    readonly body_text: string;
    readonly language: string;
    readonly risk_level: string;
    readonly status: string;
    readonly policy_reasons: readonly string[] | null;
    readonly missing_information: readonly string[] | null;
    readonly thread_id: string;
  } | null;
}

function expiresIn(iso: string): string {
  const hours = Math.round((new Date(iso).getTime() - Date.now()) / 3_600_000);
  if (hours <= 0) return 'expired';
  if (hours < 48) return `expires in ${hours}h`;
  return `expires in ${Math.round(hours / 24)}d`;
}

function ApprovalCard({
  approval,
  accessToken,
  onChanged,
}: {
  readonly approval: PendingApproval;
  readonly accessToken: string | null;
  readonly onChanged: () => void;
}) {
  const config = useRuntimeConfig();
  const [reason, setReason] = useState('');
  const draft = approval.communication_drafts;

  const decide = useMutation({
    mutationFn: (decision: 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED') =>
      apiRequest<{ ok: boolean }>(`/api/approvals/${approval.id}/decide`, {
        method: 'POST',
        accessToken,
        body: { decision, ...(reason.trim() ? { reason: reason.trim() } : {}) },
      }),
    onSuccess: onChanged,
  });

  const send = useMutation({
    mutationFn: () =>
      apiRequest<{ status: string; simulated?: boolean }>(`/api/drafts/${draft?.id}/send`, {
        method: 'POST',
        accessToken,
      }),
    onSuccess: onChanged,
  });

  const expired = new Date(approval.expires_at).getTime() <= Date.now();

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>{draft?.subject ?? '(no subject)'}</CardTitle>
          <CardDescription>
            version {approval.approved_version} · {approval.approved_recipient_count} recipient
            {approval.approved_recipient_count === 1 ? '' : 's'} · {expiresIn(approval.expires_at)}
          </CardDescription>
        </div>
        <Badge tone={draft?.risk_level === 'LOW' ? 'neutral' : 'warning'}>
          {draft?.risk_level ?? 'MEDIUM'}
        </Badge>
      </CardHeader>

      <CardContent className="space-y-3">
        <p className="rounded-md border border-slate-200 bg-white p-3 text-sm whitespace-pre-wrap text-slate-800 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
          {draft?.body_text ?? ''}
        </p>

        {draft?.policy_reasons && draft.policy_reasons.length > 0 ? (
          <ul className="space-y-1">
            {draft.policy_reasons.map((item, index) => (
              <li key={index} className="text-xs text-amber-800 dark:text-amber-300">
                • {item}
              </li>
            ))}
          </ul>
        ) : null}

        {draft?.missing_information && draft.missing_information.length > 0 ? (
          <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-950">
            <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
              Not known — deliberately not filled in
            </p>
            <ul className="mt-1 space-y-0.5">
              {draft.missing_information.map((item, index) => (
                <li key={index} className="text-xs text-slate-600 dark:text-slate-400">
                  • {item}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {expired ? (
          <p role="alert" className="text-sm text-red-700 dark:text-red-400">
            This request has expired. A fresh approval must be requested before it can be sent.
          </p>
        ) : null}

        <div className="space-y-2">
          <label htmlFor={`reason-${approval.id}`} className="block text-xs font-medium">
            Comment (optional; required in spirit when rejecting)
          </label>
          <Input
            id={`reason-${approval.id}`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why are you approving or rejecting this?"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button disabled={decide.isPending || expired} onClick={() => decide.mutate('APPROVED')}>
            Approve
          </Button>
          <Button
            variant="outline"
            disabled={decide.isPending || expired}
            onClick={() => decide.mutate('CHANGES_REQUESTED')}
          >
            Request changes
          </Button>
          <Button
            variant="danger"
            disabled={decide.isPending || expired}
            onClick={() => decide.mutate('REJECTED')}
          >
            Reject
          </Button>
        </div>

        {decide.isError ? (
          <p role="alert" className="text-sm text-red-700 dark:text-red-400">
            {decide.error instanceof ApiError ? decide.error.message : 'Could not record decision.'}
          </p>
        ) : null}

        {decide.isSuccess ? (
          <div className="space-y-2 rounded-md bg-emerald-50 p-3 dark:bg-emerald-950">
            <p className="text-sm text-emerald-900 dark:text-emerald-200">Decision recorded.</p>

            {/* Sending is a separate, deliberate act. Approving does not send:
                the executor re-verifies the approval at send time, and editing
                the draft in between invalidates it. */}
            <Button size="sm" disabled={send.isPending} onClick={() => send.mutate()}>
              {send.isPending ? 'Sending…' : 'Send now'}
            </Button>

            {config.simulatorEnabled ? (
              <p className="text-xs text-emerald-800 dark:text-emerald-300">
                Simulator active — the message will be recorded, not delivered.
              </p>
            ) : null}
          </div>
        ) : null}

        {send.isSuccess ? (
          <p role="status" className="text-sm text-emerald-800 dark:text-emerald-300">
            {send.data.status === 'skipped_duplicate'
              ? 'Already sent — the duplicate was skipped.'
              : send.data.simulated
                ? 'Recorded as a simulated send. Nothing left the platform.'
                : 'Sent.'}
          </p>
        ) : null}

        {send.isError ? (
          <p role="alert" className="text-sm text-red-700 dark:text-red-400">
            {send.error instanceof ApiError ? send.error.message : 'Could not send.'}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ApprovalsPage() {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const approvals = useQuery({
    queryKey: ['approvals'],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<{ approvals: PendingApproval[] }>('/api/approvals', { accessToken }),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Approvals</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Nothing is sent without a decision here. Editing a draft after approval invalidates it.
        </p>
      </header>

      {approvals.isPending ? <LoadingState label="Loading approvals" /> : null}

      {approvals.isError ? (
        <ErrorState
          description={
            approvals.error instanceof ApiError
              ? approvals.error.message
              : 'Could not load approvals.'
          }
          onRetry={() => void approvals.refetch()}
        />
      ) : null}

      {approvals.data && approvals.data.approvals.length === 0 ? (
        <Card>
          <EmptyState
            title="Nothing awaiting approval"
            description="Drafts appear here once someone requests approval for them."
          />
        </Card>
      ) : null}

      <ul className="space-y-3">
        {(approvals.data?.approvals ?? []).map((approval) => (
          <li key={approval.id}>
            <ApprovalCard
              approval={approval}
              accessToken={accessToken}
              onChanged={() => void queryClient.invalidateQueries({ queryKey: ['approvals'] })}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export const approvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/approvals',
  component: () => (
    <RequireSession>
      <ApprovalsPage />
    </RequireSession>
  ),
});
